import { fromIsoDate, toIsoDate } from '@app/utils/functions';

/**
 * The class overview's filter state, as it is written down: stored in a saved filter, or packed
 * into a link handed to someone else. Both are the same view leaving this session, so both are
 * read back through here.
 *
 * Kept out of Classes.tsx because none of it needs React, and the week arithmetic in particular
 * is worth testing on its own.
 */

/** One entry per filterable field, holding the values selected for it. */
export type FilterSet = Record<string, string[]>;

export type DateRange = { start: string; end: string };

/** The empty filter set: an entry per filterable field, none of them selected. */
export const emptyFilters = (): FilterSet => ({
  type: [],
  name: [],
  status: [],
  instructor: [],
  offeringId: [],
  week: [],
  language: [],
  requestId: [],
  flag: [],
  clientExecutive: [],
  region: []
});

/**
 * A filter set with every field present and holding strings.
 *
 * The table's predicates read `filters.name?.length === 0 || filters.name?.some(...)`, which
 * answers *false* for a field that is absent rather than empty - so a set arriving one key short
 * would quietly filter every class away. Values are pinned to strings for the same reason: the
 * predicates call `.toLowerCase()` on them, and anything else takes the page down.
 *
 * Everything reaching this page from outside the session - a saved filter, a shared link - comes
 * through here first. Unknown keys are dropped, which is also what removes the week anchor below.
 */
export const normalizeFilters = (filters?: FilterSet | null): FilterSet => {
  const normalized = emptyFilters();
  Object.entries(filters ?? {}).forEach(([field, values]) => {
    if (field in normalized && Array.isArray(values)) {
      normalized[field] = values.filter((value) => typeof value === 'string');
    }
  });
  return normalized;
};

// --- Week-relative saved dates ---

/**
 * Where a saved filter records the week its date range was drawn in.
 *
 * It rides inside the filter map because that is the one part of a saved filter the service keeps
 * as free-form JSON - `appliedDates` is two dated columns and has no room for a third field. The
 * key is not a filterable field, so normalizeFilters drops it on the way in and the table never
 * sees it.
 */
export const WEEK_ANCHOR_KEY = '__weekAnchor';

/**
 * The Sunday that opens the week `date` falls in, as "yyyy-MM-dd".
 *
 * Sunday because that is the week the rest of the app counts in: getDynamicWeekRange's
 * "this week" and getSundayXWeeksAgo's floor both start there.
 */
export const weekStart = (date: Date = new Date()): string => {
  const sunday = new Date(date);
  sunday.setDate(sunday.getDate() - sunday.getDay());
  return toIsoDate(sunday);
};

/**
 * Whole weeks from one week start to another.
 *
 * Through UTC midnight rather than by subtracting the dates directly: an hour lost or gained to
 * daylight saving in between makes a week 167 or 169 hours long, and the division then lands
 * just short of the week it should be.
 */
const weeksBetween = (fromIso: string, toIso: string): number => {
  const asUtc = (iso: string) => {
    const date = fromIsoDate(iso);
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  };
  return Math.round((asUtc(toIso) - asUtc(fromIso)) / (7 * 24 * 60 * 60 * 1000));
};

/** The same range moved `weeks` weeks on, keeping both ends on their weekday. */
export const shiftRangeByWeeks = (range: DateRange | null, weeks: number): DateRange | null => {
  if (!range || weeks === 0) return range;
  const shift = (iso: string) => {
    // One end alone can be set - "from the 24th", with no closing date - and stays that way.
    if (!iso) return iso;
    const date = fromIsoDate(iso);
    date.setDate(date.getDate() + weeks * 7);
    return toIsoDate(date);
  };
  return { start: shift(range.start), end: shift(range.end) };
};

/**
 * The filter set to store, stamped with the week its date range was drawn in.
 *
 * That stamp is what makes the range keep its meaning: a filter saved as "the two weeks after
 * next" should still name the two weeks after next when it is applied a month later, not the
 * fortnight it happened to be pointing at on the day it was saved. Only the stamp is stored -
 * the dates themselves stay as they were - so readSavedFilter can work out how far the weeks
 * have moved since.
 *
 * No range, no stamp: there is nothing for it to be relative to.
 */
export const withWeekAnchor = (
  filters: FilterSet,
  range: DateRange | null,
  today: Date = new Date()
): FilterSet => {
  const stored = normalizeFilters(filters);
  if (range && (range.start || range.end)) stored[WEEK_ANCHOR_KEY] = [weekStart(today)];
  return stored;
};

/**
 * A saved filter as this session should apply it: the fields the table understands, and a date
 * range brought forward to the week the user is in now.
 *
 * A filter with no anchor keeps its dates exactly as they were saved. That covers both halves of
 * the choice offered when saving - "pin these dates" writes no anchor - and every filter saved
 * before any of this existed.
 */
export const readSavedFilter = (
  saved: { filters?: FilterSet | null; appliedDates?: DateRange | null },
  today: Date = new Date()
): { filters: FilterSet; appliedDates: DateRange | null } => {
  const anchor = saved.filters?.[WEEK_ANCHOR_KEY]?.[0];
  const range = saved.appliedDates ?? null;
  return {
    filters: normalizeFilters(saved.filters),
    appliedDates: anchor ? shiftRangeByWeeks(range, weeksBetween(anchor, weekStart(today))) : range
  };
};

/**
 * Whether this saved filter's date range follows the weeks, so the list it appears in can say
 * which of two filters over the same dates will still mean the same thing next month.
 */
export const rollsWithWeeks = (saved: { filters?: FilterSet | null }): boolean =>
  !!saved.filters?.[WEEK_ANCHOR_KEY]?.[0];

// --- Shared links ---

/** The query parameter a shared view travels in. */
export const SHARE_PARAM = 'filter';

/** Everything that decides which classes the overview shows, and in what order. */
export interface SharedView {
  filters: FilterSet;
  appliedDates: DateRange | null;
  hideCancelled: boolean;
  hideNonNiitDelivery: boolean;
  sortIndex: number | null;
  sortDirection: 'asc' | 'desc' | null;
}

/**
 * Bumped only for a change the reader cannot make sense of. An older link with a version it does
 * not know is refused outright rather than half-applied, which would silently show the recipient
 * a different set of classes from the one they were sent.
 */
const SHARE_VERSION = 1;

/**
 * URL-safe base64, over UTF-8 bytes.
 *
 * btoa alone is byte-oriented and throws on anything outside Latin-1, which course names and
 * instructor names supply readily enough - so the text is encoded to bytes first. `+` and `/`
 * then become `-` and `_`, and the padding goes, so the result survives being pasted into a
 * chat message and read back out of a URL.
 */
const base64UrlEncode = (text: string): string => {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const base64UrlDecode = (encoded: string): string => {
  const binary = atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
  return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
};

/**
 * The view as one URL-safe token. Short keys and selected fields only: the whole thing ends up
 * in a link someone has to paste somewhere, and an empty field says nothing a missing one does
 * not.
 */
export const encodeSharedView = (view: SharedView): string =>
  base64UrlEncode(JSON.stringify({
    v: SHARE_VERSION,
    f: Object.fromEntries(Object.entries(view.filters).filter(([, values]) => values?.length)),
    d: view.appliedDates,
    hc: view.hideCancelled,
    hnn: view.hideNonNiitDelivery,
    si: view.sortIndex,
    sd: view.sortDirection
  }));

/**
 * The view a link was carrying, or null if it was not carrying one.
 *
 * Everything here arrived in a URL, so nothing is trusted: a truncated paste, a mangled
 * character or a link from a later version of the app all come back null, and the caller says so
 * rather than showing a view that is quietly not the one that was shared.
 */
export const decodeSharedView = (encoded: string): SharedView | null => {
  try {
    const payload = JSON.parse(base64UrlDecode(encoded));
    if (!payload || typeof payload !== 'object' || payload.v !== SHARE_VERSION) return null;

    const dates = payload.d && typeof payload.d === 'object'
      ? { start: String(payload.d.start ?? ''), end: String(payload.d.end ?? '') }
      : null;

    return {
      filters: normalizeFilters(payload.f),
      appliedDates: dates && (dates.start || dates.end) ? dates : null,
      hideCancelled: payload.hc === true,
      // The only field read the other way round: it is on unless the sender turned it off, so a
      // link written before the toggle existed opens on the same classes a fresh page does.
      hideNonNiitDelivery: payload.hnn !== false,
      sortIndex: typeof payload.si === 'number' ? payload.si : null,
      sortDirection: payload.sd === 'asc' || payload.sd === 'desc' ? payload.sd : null
    };
  } catch {
    // Not our token: unreadable base64, or bytes that are not JSON.
    return null;
  }
};

/** The link to hand over: this page, plus the view packed into its query string. */
export const shareUrl = (
  view: SharedView,
  location: { origin: string; pathname: string } = window.location
): string => `${location.origin}${location.pathname}?${SHARE_PARAM}=${encodeSharedView(view)}`;
